// Canonical new-comparability input adapter (one-shot plan §"Canonical data
// contract"): ONE place that turns the app's plan + calculated-census output
// into the normalized facts the readiness evaluator, scenario solver, and
// exports consume. The solver never re-derives HCE status, eligibility,
// compensation bases, or annual additions — it reads these rows.
//
// Money: integer CENTS everywhere downstream. Percent searches: integer BASIS
// POINTS. This module is the single place dollars are rounded into cents.
// Zero, false, missing, and not-applicable stay distinct: a blank census
// field normalizes to null (never 0), and readiness reports it as missing.

import {
  makeAssumption,
  makeLimitation,
  makeReason,
} from './compliance/complianceStatus.js'
import { RETIRA_AGE_WEIGHTED_ASSUMPTION_SET } from './compliance/ageWeightedAssumptions.js'

// ---------------------------------------------------------------------------
// Rounding, defined once (working rule: integer cents / integer basis points).
// ---------------------------------------------------------------------------

export const toCents = (dollars) => Math.round((Number(dollars) || 0) * 100)
export const fromCents = (cents) => (Number(cents) || 0) / 100
export const toBasisPoints = (percent) => Math.round((Number(percent) || 0) * 100)
export const fromBasisPoints = (bp) => (Number(bp) || 0) / 100

const isBlank = (value) => value === null || value === undefined || String(value).trim() === ''
const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value)
const finiteOrNull = (value) => (Number.isFinite(Number(value)) && !isBlank(value) ? Number(value) : null)
const tenureYearsOrNull = (value) => {
  if (isObject(value)) return finiteOrNull(value.years)
  return finiteOrNull(value)
}

// ---------------------------------------------------------------------------
// Plan facts
// ---------------------------------------------------------------------------

const GATEWAY_ROUTE_MAP = {
  gateway: 'minimum-allocation',
  'broadly-available': 'broadly-available',
  'age-service-schedule': 'gradual-age-service',
}

const TOP_HEAVY_STATUSES = ['top-heavy', 'not-top-heavy', 'unknown']

function allocationFormulaFor(plan) {
  const design = plan?.employerContributions?.profitSharing
  if (!design || design.enabled !== true) return null
  const type = design.formula?.type
  if (type === 'new_comparability') {
    return design.formula.groupMode === 'per_person' ? 'each-participant' : 'participant-groups'
  }
  if (type === 'age_weighted') return 'age-weighted'
  return type ?? null
}

// Normalize the plan + employer profile (+ live top-heavy metrics when the
// caller has them) into the plan-facts contract. Facts the app cannot know
// stay null/'unknown' — they are surfaced by readiness, never guessed.
export function normalizeNewCompPlanFacts(plan = {}, {
  employerProfile = null,
  topHeavy = null,
  // User attestation that the entered compensation is a §414(s) safe-harbor
  // definition. The app cannot verify this; attesting removes the review item
  // but records a disclosed, user-sourced assumption on every result.
  compensation414sAttested = false,
  // Combined DB/DC facts (elections + aggregation intent) supplied by the
  // designer's Advanced settings; never inferred from the plans existing.
  dbDcAggregation = null,
} = {}) {
  const planYear = Number(plan.planYear) || null
  const ndt = plan.nondiscriminationTesting || {}
  const design = plan?.employerContributions?.profitSharing || null
  const referencedGroupIds = design?.mode === 'standard' && design.formula?.type === 'new_comparability'
    ? (design.formula.groups || []).map((group) => group.groupId)
    : []
  const groupsById = new Map((plan.allocationGroups || []).map((group) => [group.id, group]))
  // Rule-based groups (owners / hces / staff / everyone-else) have
  // engine-defined objective criteria. 'local' groups are explicit name lists:
  // definitely determinable membership, but the REASONABLENESS of the
  // classification is a facts-and-circumstances call the app cannot make.
  const localGroupIds = referencedGroupIds
    .filter((groupId) => groupsById.get(groupId)?.rule === 'local')

  const profile = employerProfile || {}
  const controlledGroupStatus = profile.controlledGroupStatus ?? 'unknown'
  const affiliatedServiceGroupStatus = profile.affiliatedServiceGroupStatus ?? 'unknown'
  const completeEmployerPopulation = controlledGroupStatus === 'no' && affiliatedServiceGroupStatus === 'no'

  const topHeavySource = topHeavy?.source ?? (topHeavy?.status ? 'calculated' : null)
  return {
    planYear,
    planYearStart: planYear ? `${planYear}-01-01` : null,
    planYearEnd: planYear ? `${planYear}-12-31` : null,
    limitationYear: planYear,
    shortPlanYear: false,
    normalRetirementAge: Number(design?.formula?.normalRetirementAge) || 65,
    testingAssumptionSet: design?.formula?.assumptionSet ?? RETIRA_AGE_WEIGHTED_ASSUMPTION_SET,
    gatewayRoute: GATEWAY_ROUTE_MAP[ndt.gatewayRoute] || 'minimum-allocation',
    gatewayMinimumMethod: 'lesser-of',
    gatewayRouteQualification: isObject(ndt.gatewayRouteQualification)
      ? { ...ndt.gatewayRouteQualification }
      : null,
    allocationFormula: allocationFormulaFor(plan),
    objectiveGroupCriteriaConfirmed: localGroupIds.length === 0,
    subjectiveGroupIds: localGroupIds,
    compensationDefinition: {
      allocationBasis: 'plan-compensation-401a17-capped',
      // Audited basis (IRS EP CPE ch.10 / tier-1 fixes): BOTH gateway
      // measurements run on §415(c)(3) compensation. Distinct fields exist so
      // a future plan-supplied basis is representable, not so the engine can
      // silently pick a narrower one.
      gatewayOneThirdBasis: 'section-415',
      gatewayFivePercentBasis: 'section-415',
      testingBasis: 'plan-compensation-401a17-capped',
      // The app cannot verify a §414(s) compensation-ratio test: unknown
      // unless the user explicitly attests to a safe-harbor definition.
      safeHarbor414s: compensation414sAttested === true ? true : null,
      safeHarbor414sSource: compensation414sAttested === true ? 'user-attestation' : null,
      measurementPeriod: 'plan-year',
    },
    eligibility: plan.eligibility ? { ...plan.eligibility } : null,
    allocationConditions: design?.conditions
      ? { minHours: design.conditions.minHours ?? 0, lastDayRequired: design.conditions.lastDayRequired === true }
      : null,
    topPaidGroupElection: false,
    calendarYearElection: false,
    otherwiseExcludableElection: ndt.otherwiseExcludable === true,
    imputedDisparityElection: ndt.imputePermittedDisparity === true,
    rateBandingElection: ndt.rateBanding === true,
    aggregation: {
      completeEmployerPopulation,
      controlledGroupStatus,
      affiliatedServiceGroupStatus,
      relatedEmployerCount: null,
      otherPlanCount: profile.priorDbPlanHistory === 'yes' ? 1 : (profile.priorDbPlanHistory === 'no' ? 0 : null),
      allRequiredPlansIncluded: completeEmployerPopulation && profile.priorDbPlanHistory === 'no',
    },
    topHeavy: {
      status: TOP_HEAVY_STATUSES.includes(topHeavy?.status) ? topHeavy.status : 'unknown',
      minimumPercent: finiteOrNull(topHeavy?.minimumPercent),
      source: ['calculated', 'imported', 'assumed'].includes(topHeavySource) ? topHeavySource : null,
    },
    // A plan with an enabled cash-balance component is a DB plan alongside
    // the DC design: the combined DB/DC path is mandatory before any modeled
    // pass, and aggregation itself stays an explicit user confirmation.
    cashBalance: {
      enabled: plan.cashBalance?.enabled === true,
      interestCreditType: plan.cashBalance?.interestCredit?.type === 'index' ? 'index' : 'fixed',
      interestCreditIndex: plan.cashBalance?.interestCredit?.index ?? null,
      interestCreditRate: Number.isFinite(Number(
        plan.cashBalance?.interestCredit?.type === 'index'
          ? plan.cashBalance?.interestCredit?.assumptionRate
          : plan.cashBalance?.interestCredit?.rate))
        ? Number(plan.cashBalance.interestCredit.type === 'index'
          ? plan.cashBalance.interestCredit.assumptionRate
          : plan.cashBalance.interestCredit.rate) / 100
        : 0.05,
      interestCreditFloor: finiteOrNull(plan.cashBalance?.interestCredit?.floor),
      interestCreditCap: finiteOrNull(plan.cashBalance?.interestCredit?.cap),
      eligibility: plan.cashBalance?.eligibility ? { ...plan.cashBalance.eligibility } : null,
    },
    dbDcAggregation: dbDcAggregation && typeof dbDcAggregation === 'object'
      ? { ...dbDcAggregation }
      : null,
  }
}

// ---------------------------------------------------------------------------
// Participant facts
// ---------------------------------------------------------------------------

// Normalize calculatePlanResults() employee rows (plus the raw census rows for
// blank-vs-zero detection) into the participant contract. Never mutates its
// inputs; never invents a value for a blank field.
export function normalizeNewCompParticipants(calculatedEmployees = [], rawEmployees = []) {
  const rawById = new Map((Array.isArray(rawEmployees) ? rawEmployees : [])
    .filter((employee) => employee && employee.id)
    .map((employee) => [employee.id, employee]))
  return (Array.isArray(calculatedEmployees) ? calculatedEmployees : [])
    .filter(Boolean)
    .map((employee) => {
      const hasRawRow = rawById.has(employee.id)
      const raw = rawById.get(employee.id) || {}
      // Blank census compensation is MISSING, never zero. Detectable only when
      // the raw census row is supplied; without it we do not guess.
      const compensationBlank = hasRawRow && isBlank(raw.compensation)
      const allocationCompensation = Number(employee.cappedCompensation) || 0
      const raw415 = Number(employee.section415Compensation)
      const explicit415 = employee.section415CompensationSource === 'explicit'
        || !isBlank(raw.section_415_compensation)
      const section415Compensation = explicit415 && Number.isFinite(raw415)
        ? raw415
        : Number(employee.gatewayCompensation) || allocationCompensation
      const limit415c = Number(employee.limit415c) || 0
      const deferralCents = toCents(employee.baseDeferral)
      const catchUpCents = toCents(employee.catchupContribution)
      const matchCents = toCents(employee.matchAmount)
      const nonelectiveCents = toCents(employee.necAmount)
      // Keep the safe-harbor NEC source intact all the way into the solver.
      // The total gateway allocation is unchanged, but the split is required
      // to prove that NEC received credit while safe-harbor match did not.
      const safeHarborNonelectiveCents = employee.necIsSafeHarbor === true
        ? nonelectiveCents
        : 0
      const otherNonelectiveCents = nonelectiveCents - safeHarborNonelectiveCents
      const qnecCents = toCents(employee.qnecAmount)
      const forfeitureCents = toCents(employee.forfeitureAllocation)
      const afterTaxCents = toCents(employee.afterTaxContribution)
      const otherPlanCents = toCents(employee.otherPlanAnnualAdditions)
      // §415(c) annual additions BEFORE any new profit sharing: deferrals
      // (excluding catch-up), employer money, after-tax, reallocated
      // forfeitures, and any supplied other-plan additions.
      const priorAdditionsCents = deferralCents + matchCents + nonelectiveCents
        + qnecCents + forfeitureCents + afterTaxCents + otherPlanCents
      const capCents = Math.min(toCents(limit415c), toCents(section415Compensation))
      return {
        id: employee.id,
        name: employee.name || '',
        entityId: null,
        age: finiteOrNull(employee.age),
        dateOfBirth: raw.dob || null,
        hireDate: employee.hireDate || null,
        entryDate: employee.cashBalanceParticipantEntryDate || raw.cb_participant_entry_date || null,
        cashBalanceOpeningBalanceCents: toCents(
          employee.cashBalanceOpeningBalance ?? raw.cb_opening_balance),
        priorAccruedBenefitMonthly: finiteOrNull(
          employee.priorAccruedBenefitMonthly ?? raw.cb_prior_accrued_monthly_benefit),
        priorVestingYears: finiteOrNull(
          employee.priorVestingYears ?? raw.cb_prior_vesting_years),
        terminationDate: employee.terminationDate || null,
        hours: finiteOrNull(employee.hoursServed),
        employmentStatus: employee.status || null,
        isUnion: false,
        isNonresidentAlien: false,
        isHCE: employee.isHCE === true,
        hceReason: employee.isHCE
          ? (employee.isFivePercentOwner ? 'owner' : 'compensation')
          : null,
        hceIsEstimated: employee.hceIsEstimated === true,
        isKey: employee.keyEmployeeStatus === 'key',
        keyEmployeeStatus: employee.keyEmployeeStatus ?? 'unknown',
        ownership: finiteOrNull(employee.ownershipPercent),
        familyAttribution: employee.attributedOwnershipFrom || null,
        nonexcludableForCoverage: !['ineligible', 'ltpt'].includes(employee.eligibility?.status),
        eligibilityStatus: employee.eligibility?.status ?? 'unknown',
        otherwiseExcludable: employee.otherwiseExcludable === true,
        benefitsUnderPlan: (Number(employee.employerTotal) || 0)
          + (Number(employee.qnecAmount) || 0) + (Number(employee.forfeitureAllocation) || 0) > 0,
        profitSharingEligible: employee.eligibility?.allowsEmployerContributions !== false
          && employee.profitSharingConditionMet !== false,
        allocationConditionMet: employee.profitSharingConditionMet !== false,
        compensationBlank,
        allocationCompensation,
        testingCompensation: allocationCompensation,
        section415Compensation,
        deductionCompensation: allocationCompensation,
        priorYearCompensation: finiteOrNull(raw.prior_year_compensation),
        section415CompensationSource: employee.section415CompensationSource || 'unknown',
        // Cents fields — everything the solver adds or reduces is integer cents.
        deferralCents,
        catchUpCents,
        matchCents,
        safeHarborNonelectiveCents,
        otherNonelectiveCents,
        qnecCents,
        qmacCents: 0,
        forfeitureCents,
        afterTaxCents,
        otherPlanAnnualAdditionsCents: otherPlanCents,
        limit415cCents: toCents(limit415c),
        remaining415cHeadroomCents: Math.max(0, capCents - priorAdditionsCents),
        selfEmployedFacts: raw.self_employed === true || raw.entity_type === 'self-employed'
          ? { detected: true, earnedIncomeInputsPresent: false }
          : null,
        testingAge: employee.ageWeightedTestingAge ?? undefined,
        // Combined DB/DC inputs: the adopted cash-balance credit and the DB
        // limit facts the level-credit solve needs.
        cashBalanceCreditCents: toCents(employee.cashBalanceCredit),
        high3: finiteOrNull(employee.high3Compensation),
        serviceYears: tenureYearsOrNull(employee.tenure),
      }
    })
}

// ---------------------------------------------------------------------------
// Readiness (one-shot plan §"Readiness before solving")
// ---------------------------------------------------------------------------

const NEXT_ACTIONS = {
  'missing-dob': 'Add a date of birth (or age) on the Census tab for each listed employee.',
  'missing-compensation': 'Enter plan-year compensation for each listed employee (blank is not zero).',
  'missing-plan-year': 'Pick the plan year in Plan settings.',
  'no-participants': 'Add employees to the census before designing an allocation.',
  'no-hce': 'Mark at least one owner or HCE (Owner >5%, Own %, or prior-year compensation).',
  'no-nhce': 'A cross-tested design needs at least one eligible non-HCE employee.',
  'profit-sharing-disabled': 'Turn on profit sharing with the New comparability formula for this plan.',
  'related-employers-incomplete': 'Confirm the census covers every related employer, or mark the controlled-group answer in the employer profile.',
  'related-employers-unknown': 'Answer the controlled-group and affiliated-service-group questions in the employer profile.',
  'other-plans-unknown': 'Confirm whether the employer sponsors another plan; other-plan annual additions change §415(c) headroom.',
  'top-heavy-unknown': 'Supply prior-year balances and key-employee flags (or record a documented top-heavy assumption).',
  'compensation-definition-unverified': 'Confirm the plan uses a safe-harbor §414(s) compensation definition, or have the TPA run the compensation-ratio test.',
  'subjective-groups': 'Allocation groups defined by name list need the TPA to confirm the classification is reasonable and objective.',
  'self-employed-facts': 'Self-employed/partner compensation needs earned-income inputs the app does not have; treat these rows as W-2 only after professional review.',
  'hce-estimated': 'Add prior-year compensation (or ownership) so HCE status is determined, not estimated.',
  'combined-testing-required': 'This plan has an enabled cash-balance component. Confirm DB/DC aggregation in the designer (Advanced settings) so the combined tests run; a DC-only result can never be a modeled pass here.',
}

const item = (code, message, { employeeIds, field, severity = 'blocking' } = {}) => ({
  code,
  message,
  employeeIds: Array.isArray(employeeIds) && employeeIds.length ? employeeIds : undefined,
  field: field ?? null,
  severity,
  nextAction: NEXT_ACTIONS[code] || null,
})

// Pure readiness evaluation. Blockers stop the solver (a missing field could
// materially change targets, headroom, gateway cost, coverage, or rate
// groups); review items downgrade the eventual result to requires-review but
// do not stop modeling.
export function evaluateNewCompReadiness({ planFacts = null, participants = [] } = {}) {
  const blockers = []
  const reviewItems = []
  const rows = Array.isArray(participants) ? participants : []

  if (!planFacts?.planYear) {
    blockers.push(item('missing-plan-year', 'The plan year is not set.', { field: 'planYear' }))
  }
  if (!rows.length) {
    blockers.push(item('no-participants', 'The census has no employees.'))
  }

  const included = rows.filter((row) => row.employmentStatus !== 'Terminated' || row.benefitsUnderPlan)
  const missingDob = rows.filter((row) => row.age === null)
  if (missingDob.length) {
    blockers.push(item('missing-dob',
      `${missingDob.length} employee${missingDob.length === 1 ? ' is' : 's are'} missing a date of birth, so EBARs cannot be computed.`,
      { employeeIds: missingDob.map((row) => row.id), field: 'dob' }))
  }
  const missingComp = rows.filter((row) => row.compensationBlank)
  if (missingComp.length) {
    blockers.push(item('missing-compensation',
      `${missingComp.length} employee${missingComp.length === 1 ? ' has' : 's have'} blank compensation. Blank is treated as missing, never as zero.`,
      { employeeIds: missingComp.map((row) => row.id), field: 'compensation' }))
  }
  const hces = rows.filter((row) => row.isHCE)
  const nhces = rows.filter((row) => !row.isHCE)
  if (rows.length && !hces.length) {
    blockers.push(item('no-hce', 'No HCE or owner was identified, so there is no one to target.'))
  }
  if (rows.length && !nhces.length) {
    blockers.push(item('no-nhce', 'No non-HCE employees were found; the general test cannot run.'))
  }
  if (planFacts && planFacts.allocationFormula === null) {
    blockers.push(item('profit-sharing-disabled', 'Profit sharing is not enabled on this plan.', { field: 'employerContributions.profitSharing' }))
  }

  // ---- Review items (professional / document facts) ----
  const aggregation = planFacts?.aggregation
  if (aggregation) {
    if (aggregation.controlledGroupStatus === 'yes' || aggregation.affiliatedServiceGroupStatus === 'yes') {
      reviewItems.push(item('related-employers-incomplete',
        'The employer is part of a controlled or affiliated service group. Testing must cover the full related-employer population, which this census may not include.',
        { severity: 'review', field: 'employerProfile' }))
    } else if (aggregation.controlledGroupStatus === 'unknown' || aggregation.affiliatedServiceGroupStatus === 'unknown') {
      reviewItems.push(item('related-employers-unknown',
        'Controlled-group / affiliated-service-group status is unanswered, so the tested population cannot be confirmed complete.',
        { severity: 'review', field: 'employerProfile' }))
    }
    if (aggregation.otherPlanCount === null) {
      reviewItems.push(item('other-plans-unknown',
        'Whether the employer sponsors another plan is unanswered; required aggregation and other-plan §415(c) additions cannot be confirmed.',
        { severity: 'review', field: 'employerProfile' }))
    } else if (aggregation.otherPlanCount > 0) {
      reviewItems.push(item('other-plans-unknown',
        'The employer has (or had) another plan. Its annual additions and any required aggregation are not in this model.',
        { severity: 'review', field: 'employerProfile' }))
    }
  }
  if (planFacts && planFacts.compensationDefinition?.safeHarbor414s !== true) {
    reviewItems.push(item('compensation-definition-unverified',
      'Compensation is used as entered; a §414(s) safe-harbor definition (or a passing compensation-ratio test) is not verified.',
      { severity: 'review', field: 'compensationDefinition' }))
  }
  if (planFacts && !planFacts.objectiveGroupCriteriaConfirmed) {
    reviewItems.push(item('subjective-groups',
      'One or more allocation groups are defined by an explicit name list; the reasonableness of that classification is a facts-and-circumstances determination.',
      { severity: 'review', field: 'allocationGroups' }))
  }
  if (planFacts?.topHeavy?.status === 'unknown') {
    reviewItems.push(item('top-heavy-unknown',
      'Top-heavy status is unknown (prior balances, distributions, or key-employee facts are incomplete); the minimum-allocation consequence is not modeled.',
      { severity: 'review', field: 'topHeavy' }))
  } else if (planFacts?.topHeavy?.source === 'assumed') {
    reviewItems.push(item('top-heavy-unknown',
      `Top-heavy status is an ASSUMPTION (${planFacts.topHeavy.status}); formal determination needs prior balances, distributions, and aggregation facts.`,
      { severity: 'review', field: 'topHeavy' }))
  }
  const estimatedHces = hces.filter((row) => row.hceIsEstimated)
  if (estimatedHces.length) {
    reviewItems.push(item('hce-estimated',
      `${estimatedHces.length} HCE determination${estimatedHces.length === 1 ? ' is' : 's are'} estimated from current-year pay because prior-year compensation is missing.`,
      { severity: 'review', employeeIds: estimatedHces.map((row) => row.id), field: 'prior_year_compensation' }))
  }
  const selfEmployed = rows.filter((row) => row.selfEmployedFacts?.detected)
  if (selfEmployed.length) {
    reviewItems.push(item('self-employed-facts',
      `${selfEmployed.length} participant${selfEmployed.length === 1 ? ' appears' : 's appear'} to be self-employed. Earned-income (net SE income less ½ SE tax and the contribution circularity) is not modeled; compensation was used as entered.`,
      { severity: 'review', employeeIds: selfEmployed.map((row) => row.id) }))
  }
  if (planFacts?.cashBalance?.enabled && planFacts?.dbDcAggregation?.aggregate !== true) {
    reviewItems.push(item('combined-testing-required',
      'An enabled cash-balance component means combined DB/DC testing is required before any modeled pass. Aggregation has not been confirmed yet.',
      { severity: 'review', field: 'cashBalance' }))
  }

  const blocked = blockers.length > 0
  const conditional = reviewItems.length > 0
  const availability = (needed = true) => (blocked ? 'unavailable' : (needed && conditional ? 'conditional' : 'available'))
  return {
    status: blocked ? 'blocked' : 'ready',
    blockers,
    reviewItems,
    includedCount: included.length,
    hceCount: hces.length,
    nhceCount: nhces.length,
    testsAvailability: {
      gateway: availability(),
      coverage: availability(),
      rateGroups: availability(),
      abpt: availability(),
      section415c: blocked ? 'unavailable' : 'available',
      deduction: blocked ? 'unavailable' : 'available',
      topHeavy: planFacts?.topHeavy?.status === 'unknown' ? 'conditional' : (blocked ? 'unavailable' : 'available'),
    },
  }
}

// Readiness → the assumptions/limitations the eventual scenario status carries.
export function readinessDisclosures(readiness, planFacts) {
  const assumptions = []
  const limitations = []
  for (const entry of readiness?.reviewItems || []) {
    limitations.push(makeLimitation(entry.code, entry.message, 'review'))
  }
  if (planFacts?.topHeavy?.source === 'assumed') {
    assumptions.push(makeAssumption('top-heavy-assumed',
      'Top-heavy status assumed', planFacts.topHeavy.status, { source: 'user', estimated: true }))
  }
  if (planFacts?.compensationDefinition?.safeHarbor414sSource === 'user-attestation') {
    assumptions.push(makeAssumption('compensation-414s-attested',
      'Compensation attested as a §414(s) safe-harbor definition', true,
      { source: 'user', estimated: false }))
  }
  return { assumptions, limitations }
}

export function readinessBlockerReasons(readiness) {
  return (readiness?.blockers || []).map((blocker) => makeReason(blocker.code, blocker.message, {
    employeeIds: blocker.employeeIds,
    field: blocker.field,
  }))
}
